import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { buildPreview, PREVIEW_SANDBOX, type BridgeMessage, type WatchedValue } from "@buildlab/preview";
import type { FileMap } from "@buildlab/shared";
import { answerClassDataRequest, answerDataRequest, api } from "./api";
import { ConsoleBar } from "./ConsoleBar";
import { useFeature } from "./features";
import { BigExit, FitControls, useFit } from "./FitFrame";
import { Restart } from "./icons";

// The sandboxed preview (PRD FR-12 to FR-17) with its console panel, freeze guard (FR-16)
// and the navigation reset from FR-12a.

export interface ConsoleLine {
  id: number;
  level: "log" | "warn" | "error";
  text: string;
}

let lineId = 0;
let buildCounter = 0;

export interface WatchUpdate {
  build: number;
  values: Record<string, WatchedValue>;
  runs: Record<string, number>;
}

export function Preview({
  projectId,
  files,
  onErrorsChange,
  onSendErrors,
  watchNames,
  onWatch,
  caption,
  compact = false,
  banner,
  cover,
  errorDetails = true,
}: {
  projectId: string;
  files: FileMap;
  onErrorsChange: (errors: string[]) => void;
  onSendErrors?: () => void;
  /** Top-level variables the code view wants live values for; polled while non-empty. */
  watchNames?: string[];
  onWatch?: (w: WatchUpdate) => void;
  caption?: string;
  /** The compact layout (useCompact): the console folds to one line. */
  compact?: boolean;
  /** Shown between the header and the game (the full-screen tip). */
  banner?: React.ReactNode;
  /** Shown instead of the running program until it's dismissed (Predict before play, D47). */
  cover?: ReactNode;
  /** The console's line-by-line messages (D46: unlocked in Lesson 4). Without them, one summary line. */
  errorDetails?: boolean;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const savedData = useRef<Record<string, unknown>>({});
  const [run, setRun] = useState(0); // bump to rebuild and restart the program
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [frozen, setFrozen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const loads = useRef(0);
  const lastPong = useRef(Date.now());

  // Projects remember (rememberData): saved values load before the first build, so the program never
  // runs with empty data and writes its defaults over a saved high score. Each saveData call is
  // written back, debounced 500 ms per key.
  const remember = useFeature("rememberData");
  const [loadedFor, setLoadedFor] = useState<string | null>(null); // the project whose saved data is in place
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const pendingSaves = useRef(new Map<string, { timer: number; projectId: string; key: string; value: unknown }>());
  const persistTo = useRef<string | null>(null);
  persistTo.current = remember ? projectId : null;
  const ready = loadedFor === projectId;

  const writeSaved = (pid: string, key: string, value: unknown) => api.saveValue(pid, key, value).catch((err: Error) => setNotice(err.message));
  const queueSave = (pid: string, key: string, value: unknown) => {
    const id = `${pid}\n${key}`;
    window.clearTimeout(pendingSaves.current.get(id)?.timer);
    const timer = window.setTimeout(() => {
      pendingSaves.current.delete(id);
      void writeSaved(pid, key, value);
    }, 500);
    pendingSaves.current.set(id, { timer, projectId: pid, key, value });
  };
  const flushSaves = () => {
    for (const p of pendingSaves.current.values()) {
      window.clearTimeout(p.timer);
      void writeSaved(p.projectId, p.key, p.value);
    }
    pendingSaves.current.clear();
  };
  const clearRemembered = async () => {
    if (!window.confirm("Clear everything this project remembers? Saved high scores start over.")) return;
    for (const p of pendingSaves.current.values()) window.clearTimeout(p.timer);
    pendingSaves.current.clear();
    try {
      await api.clearSaved(projectId);
      savedData.current = {};
      setSavedCount(0);
      setRun((r) => r + 1); // restart, so the score on screen starts over too
    } catch (err) {
      setNotice((err as Error).message);
    }
  };

  // Load this project's saved data when the project changes (or after "Try again").
  useEffect(() => {
    savedData.current = {};
    setSavedCount(0);
    setLoadError(false);
    if (!remember) {
      setLoadedFor(projectId);
      return;
    }
    let alive = true;
    api
      .savedData(projectId)
      .then(({ data }) => {
        if (!alive) return;
        savedData.current = data;
        setSavedCount(Object.keys(data).length);
        setLoadedFor(projectId);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
      flushSaves();
    };
    // flushSaves only touches refs, so it's safe to leave out of the deps.
  }, [projectId, remember, retry]);

  // Every build gets a new iframe, so a reload always means "the program navigated" (FR-12a).
  // Run counters are always on, so the code view has counts as soon as it opens.
  // Multi-page sites: links between the project's pages switch the page shown here.
  const pages = Object.keys(files).filter((f) => /\.html?$/i.test(f)).sort((a, b) => (a === "index.html" ? -1 : b === "index.html" ? 1 : a.localeCompare(b)));
  const [page, setPage] = useState("index.html");
  const shownPage = files[page] !== undefined ? page : "index.html";
  const build = useMemo(() => ({ ...buildPreview(files, { savedData: savedData.current, trace: true, page: shownPage }), id: ++buildCounter }), [files, run, shownPage, ready]);
  const buildId = useRef(build.id);
  buildId.current = build.id;
  const fit = useFit(frame, build.id);
  const projectRef = useRef(projectId);
  projectRef.current = projectId;
  const onWatchRef = useRef(onWatch);
  onWatchRef.current = onWatch;

  // A fresh build resets the console and the guards.
  useEffect(() => {
    loads.current = 0;
    lastPong.current = Date.now();
    setFrozen(false);
    setLines(build.warnings.map((text) => ({ id: ++lineId, level: "warn", text })));
  }, [build]);

  useEffect(() => {
    onErrorsChange(lines.filter((l) => l.level === "error").map((l) => l.text).slice(-20));
  }, [lines, onErrorsChange]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const m = e.data as BridgeMessage;
      if (!m || m.__buildlab !== 1) return;
      if (m.kind === "pong" || m.kind === "ready") {
        lastPong.current = Date.now();
        return;
      }
      if (m.kind === "navigate") {
        setPage(m.page);
        return;
      }
      if (m.kind === "getData") {
        answerDataRequest(frame.current.contentWindow!, m);
        return;
      }
      if (m.kind === "classData") {
        answerClassDataRequest(frame.current.contentWindow!, m, { projectId: projectRef.current }); // so unsure values can wait for the teacher
        return;
      }
      if (m.kind === "watch") {
        onWatchRef.current?.({ build: buildId.current, values: m.values, runs: m.runs });
        return;
      }
      if (m.kind === "saveData") {
        const isNew = !(m.key in savedData.current);
        savedData.current = { ...savedData.current, [m.key]: m.value };
        if (isNew) setSavedCount(Object.keys(savedData.current).length);
        if (persistTo.current) queueSave(persistTo.current, m.key, m.value);
        return;
      }
      const line: ConsoleLine | null =
        m.kind === "console"
          ? { id: ++lineId, level: m.level, text: m.text }
          : m.kind === "error"
            ? { id: ++lineId, level: "error", text: `${m.file ? `${m.file}${m.line ? `:${m.line}` : ""} ` : ""}${m.text}` }
            : m.kind === "blocked"
              ? { id: ++lineId, level: "warn", text: `Blocked a ${m.what}: ${m.detail}` }
              : null;
      if (line) setLines((ls) => [...ls.slice(-199), line]);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Heartbeat: if the program stops answering for 3 seconds, offer Stop (FR-16).
  useEffect(() => {
    let id = 0;
    const timer = setInterval(() => {
      const w = frame.current?.contentWindow;
      if (!w) return;
      w.postMessage({ __buildlabPing: 1, id: ++id }, "*");
      setFrozen(Date.now() - lastPong.current > 3000);
    }, 1000);
    return () => clearInterval(timer);
  }, [build]);

  // Code view: ask the program for its current values a few times a second.
  const watchKey = (watchNames ?? []).join(",");
  useEffect(() => {
    if (!watchKey) return;
    const names = watchKey.split(",");
    let id = 0;
    const ask = () => frame.current?.contentWindow?.postMessage({ __buildlabWatch: 1, id: ++id, names }, "*");
    const first = setTimeout(ask, 150);
    const timer = setInterval(ask, 400);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [build, watchKey]);

  const onLoad = () => {
    loads.current++;
    // FR-12a: a second load without a rebuild means the program navigated the frame away.
    if (loads.current > 1) {
      setNotice("BuildLab previews can't open other pages or websites. The preview was reset.");
      setRun((r) => r + 1);
    }
  };

  return (
    <section className="preview" aria-label="Preview">
      <div className="pane-head">
        <h2 className="eyebrow">Preview</h2>
        {pages.length > 1 && (
          <label className="page-pick">
            <span className="sr-only">Page</span>
            <select value={shownPage} onChange={(e) => setPage(e.target.value)} aria-label="Page">
              {pages.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}
        {remember && (
          <span className="saved-data small">
            Saved data: {savedCount} ·{" "}
            <button type="button" className="linklike" onClick={clearRemembered} disabled={savedCount === 0}>
              Clear
            </button>
          </span>
        )}
        {caption && <span className="muted small caption">{caption}</span>}
        <FitControls fit={fit} />
        <button type="button" className="ghost" onClick={() => setRun((r) => r + 1)} title="Run the program again from the start">
          <Restart size={14} />
          <span className="bar-label">Restart</span>
        </button>
      </div>
      {banner}
      <div ref={fit.stageRef} className={`stage ${fit.stageClass}`}>
        {cover ? (
          cover
        ) : ready ? (
          <iframe key={build.id} ref={frame} title="Your project" sandbox={PREVIEW_SANDBOX} srcDoc={build.html} onLoad={onLoad} style={fit.frameStyle} />
        ) : loadError ? (
          <div className="saved-wait" role="alert">
            <p>Couldn't load this project's saved data.</p>
            <button type="button" onClick={() => setRetry((r) => r + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <p className="saved-wait muted">Loading saved data…</p>
        )}
        {frozen && (
          <div className="frozen" role="alert">
            <p>Your program seems frozen.</p>
            <button type="button" onClick={() => setRun((r) => r + 1)}>
              Stop it
            </button>
          </div>
        )}
        <BigExit fit={fit} />
      </div>
      {notice && (
        <p className="notice" role="status">
          {notice} <button type="button" className="linklike" onClick={() => setNotice(null)}>OK</button>
        </p>
      )}
      <ConsoleBar lines={lines} onSendErrors={onSendErrors} compact={compact} errorDetails={errorDetails} />
    </section>
  );
}
